import {
    ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
    ONLINE_BOOKING_ADD_PET,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_RESET,
    ONLINE_BOOKING_SET_AGREEMENT_SIGNATURES,
    ONLINE_BOOKING_SET_AVAILABILITY,
    ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE,
    ONLINE_BOOKING_SET_BOOKING_ITEMS,
    ONLINE_BOOKING_SET_CUSTOMER,
    ONLINE_BOOKING_SET_DATE,
    ONLINE_BOOKING_SET_LOADING,
    ONLINE_BOOKING_SET_SELECTED_STAFF_ID,
    ONLINE_BOOKING_SET_STAFFS,
    ONLINE_BOOKING_SET_STAFFS_BUSY,
    ONLINE_BOOKING_SET_STAFFS_FETCHED,
    ONLINE_BOOKING_SET_STAFFS_LOADING,
    ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE,
    ONLINE_BOOKING_SET_STEP,
    ONLINE_BOOKING_SET_TIME,
    ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON,
    ONLINE_BOOKING_UPDATE_PET_START,
    ONLINE_BOOKING_UPDATE_PET_SUCCESS,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { OnlineBookingState, OnlineBookingStatus } from 'reducers/onlineBooking/OnlineBookingState';
import { Reducer } from 'redux';
import { emptyCustomer } from 'types/Customer';
import { OnlineBookingStep } from 'types/OnlineBooking';

const ONLINE_BOOKING_CHANNEL = 'online-booking';

const initialState: OnlineBookingState = {
    count: 0,
    loading: false,
    step: OnlineBookingStep.Home,
    status: OnlineBookingStatus.Initial,

    isReturningClient: false,
    customer: emptyCustomer(ONLINE_BOOKING_CHANNEL),

    availability: [],

    bookingItems: [],
    selectedStaffId: null,

    pets: [],
    staffs: [],

    agreementSignatures: []
};

export const onlineBookingReducer: Reducer<OnlineBookingState, OnlineBookingAction> = (
    state = initialState,
    action
) => {
    switch (action.type) {
        case ONLINE_BOOKING_RESET:
            return initialState;
        case ONLINE_BOOKING_SET_STEP:
            return {
                ...state,
                step: action.payload.step
            };
        case ONLINE_BOOKING_SET_LOADING:
            return {
                ...state,
                loading: action.payload.loading
            };
        case ONLINE_BOOKING_SET_CUSTOMER:
            return {
                ...state,
                customer: action.payload.customer,
                pets: action.payload.customer.pets ?? [],
                isReturningClient: action.payload.isReturningClient
            };
        case ONLINE_BOOKING_SET_DATE:
            return {
                ...state,
                time: undefined,
                date: action.payload.date
            };
        case ONLINE_BOOKING_SET_TIME:
            return {
                ...state,
                time: action.payload.time
            };
        case ONLINE_BOOKING_SET_AGREEMENT_SIGNATURES:
            return {
                ...state,
                agreementSignatures: [...action.payload.signatures]
            };
        case ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE:
            return {
                ...state,
                agreementSignatures: state.agreementSignatures.some(
                    sig => sig.uuid === action.payload.signature.uuid
                )
                    ? state.agreementSignatures.map(sig =>
                          sig.uuid === action.payload.signature.uuid
                              ? action.payload.signature
                              : sig
                      )
                    : [...state.agreementSignatures, action.payload.signature]
            };
        case ONLINE_BOOKING_SET_BOOKING_ITEMS:
            return {
                ...state,
                bookingItems: [...action.payload.items]
            };
        case ONLINE_BOOKING_ADD_PET:
            return {
                ...state,
                pets: [...state.pets, action.payload.pet]
            };
        case ONLINE_BOOKING_UPDATE_PET_START:
            return {
                ...state,
                loading: true,
                status: OnlineBookingStatus.UpdatePet
            };
        case ONLINE_BOOKING_UPDATE_PET_SUCCESS:
            return {
                ...state,
                loading: false,
                status: OnlineBookingStatus.UpdatePetSuccess,
                pets: [
                    ...state.pets.map(pet => {
                        if (pet.uuid === action.payload.pet.uuid) {
                            return action.payload.pet;
                        }

                        return pet;
                    })
                ]
            };
        case ONLINE_BOOKING_SET_BOOKING_ITEM_SERVICE:
            return {
                ...state,
                bookingItems: [
                    ...state.bookingItems.map(item => {
                        if (item.pet.uuid === action.payload.petUuid) {
                            return { ...item, service: action.payload.service };
                        }

                        return { ...item };
                    })
                ]
            };
        case ONLINE_BOOKING_TOGGLE_BOOKING_ITEM_ADDON:
            return {
                ...state,
                bookingItems: [
                    ...state.bookingItems.map(item => {
                        if (item.pet.uuid === action.payload.petUuid) {
                            return {
                                ...item,
                                addons: action.payload.isSelected
                                    ? [
                                          ...item.addons.filter(
                                              addon => addon.id !== action.payload.addon.id
                                          )
                                      ]
                                    : [...item.addons, action.payload.addon]
                            };
                        }

                        return { ...item };
                    })
                ]
            };
        case ONLINE_BOOKING_SET_SELECTED_STAFF_ID:
            return {
                ...state,
                selectedStaffId: action.payload.staffId
            };
        case ONLINE_BOOKING_SET_STAFFS:
            return {
                ...state,
                count: 0,
                staffs: action.payload.staffs.map(staff => ({
                    staff: staff,
                    loading: false,
                    fetched: false
                }))
            };
        case ONLINE_BOOKING_SET_STAFFS_LOADING:
            return {
                ...state,
                staffs: [
                    ...state.staffs.map(item => {
                        let loadingState = item.loading;

                        if (typeof action.payload.loadingState === 'boolean') {
                            loadingState = action.payload.loadingState;
                        }

                        if (
                            action.payload.loadingState instanceof Map &&
                            action.payload.loadingState.has(item.staff.id)
                        ) {
                            loadingState = action.payload.loadingState.get(item.staff.id) ?? false;
                        }

                        return {
                            ...item,
                            loading: loadingState
                        };
                    })
                ]
            };
        case ONLINE_BOOKING_SET_STAFFS_NEXT_AVAILABLE_DATE:
            return {
                ...state,
                count: state.count + 1,
                staffs: [
                    ...state.staffs.map(item => {
                        const hoursByStaff = action.payload.hours.filter(hour =>
                            hour.staffAvailable.includes(item.staff.id)
                        );

                        if (hoursByStaff.length === 0) {
                            return { ...item };
                        }

                        return {
                            ...item,
                            fetched: true,
                            loading: false,
                            busyForWeeks: undefined,
                            nextAvailableAt: {
                                date: hoursByStaff[0].datetime,
                                hours: hoursByStaff
                            }
                        };
                    })
                ]
            };
        case ONLINE_BOOKING_SET_STAFFS_FETCHED:
            return {
                ...state,
                staffs: [
                    ...state.staffs.map(item =>
                        action.payload.staffIds.includes(item.staff.id)
                            ? { ...item, fetched: true }
                            : { ...item }
                    )
                ]
            };
        case ONLINE_BOOKING_SET_STAFFS_BUSY:
            return {
                ...state,
                staffs: [
                    ...state.staffs.map(item => {
                        const staffPayload = action.payload.staffs.find(
                            payloadItem => payloadItem.staffId === item.staff.id
                        );

                        if (!staffPayload) {
                            return { ...item };
                        }

                        return {
                            ...item,
                            fetched: true,
                            loading: false,
                            busyForWeeks: staffPayload.weeksCount
                        };
                    })
                ]
            };
        case ONLINE_BOOKING_SET_AVAILABILITY:
            return {
                ...state,
                availability: action.payload.availability
            };
        case ONLINE_BOOKING_ERROR:
            return {
                ...state,
                status: OnlineBookingStatus.Error,
                loading: false
            };
        default:
            return state;
    }
};
